import { set } from 'lodash';

const $inject = ['$http', '$q', '$rootScope', 'apiService', 'settingsService', 'dateTimeService', 'APP_CONFIG', '$stateParams'];

const travelService = function travelService(
  $http,
  $q,
  $rootScope,
  apiService,
  settingsService,
  dateTimeService,
  APP_CONFIG,
  $stateParams
) {
  const service = {
    results: [],
    packageResults: [],
    fareClasses: [],
    flightMessages: [],
    hasLoaded: false,
    hasResults: false,
    hasPackages: false,
    hasBackendError: false,
    policyMessagePopups: [],
    pkgFareClasses: [],
    policyViolations: [],
    hasExchangeTicketNumber: false,
    showPackages: showPackages,
    setShowPackages: setShowPackages,
    forceShowPackages: forceShowPackages,
    init: init,
    getResults: getResults,
    setResults: setResults,
    getSeatMap: getSeatMap,
    getSeatmapSearches: getSeatmapSearches,
    getTravelDetail: getTravelDetail,
    getFareRules: getFareRules,
    prepData: prepData,
    getFareIsOutOfPolicyMsg: getFareIsOutOfPolicyMsg,
    setPolicyMsgViolations: setPolicyMsgViolations,
    setPackageResults: (results) => (service.packageResults = results),
    getPackageResults: () => service.packageResults,
    getMatchingIndexes: getMatchingIndexes,
    hasRestrictedDestination,
    hasRestrictedRoute,
    hasInvalidRestrictedDestinationValidator,
    getArray,
    SafScoreToolTipContent:
      'Sustainable aviation fuel score based on this airline\'s commitments, purchases and adoption of alternative jet fuels. <br> <a href="https://www.travelctm.com/global/blog/co2-calculations-business-travel/" target="_blank"> About the rating system </a>',
    getCleanFare: getCleanFare,
    isMatchingFare: isMatchingFare,
  };

  $rootScope.$on('cartService:rail:removed', service.checkFareFilters);

  function init() {
    service.results = [];
    service.flightMessages = [];
    service.fareClasses = [];
    service.hasLoaded = false;
    service.hasPackages = false;
    service.hasResults = false;
    service.showFareCode = settingsService.settings.FlightConfiguration.EnableFareCodeDisplay || false;
    service.hasExchangeTicketNumber = false;
  }

  function getResults(searches, callback) {
    let searchRequest = _.clone(searches);
    service.hasBackendError = false;
    service.hasLoaded = false;
    service.hasPackages = false;
    const apiUrl = apiService.getUrl('travel');

    _.forEach(searchRequest, (search) => {
      search.PaxCount = search.Passengers.length;
      search.Searches = [
        {
          Identifier: search.Identifier,
          Type: 'Flight',
        },
      ];
    });

    $http[apiService.isMockActive('travel') ? 'get' : 'post'](apiUrl, searchRequest, { errorMessage: 'searching for travel' })
      .then((response) => {
        let data = response.data;
        service.flightMessages = data.DisplayMessages;
        service.policyMessagePopups = data.PolicyMessagePopups;
        service.policyViolations = data.PolicyViolations;
        service.packageResults = [];
        service.hasPackages = false;
        const trips = data.TravelAvailabilityResponses.map((result, resultIndex) =>
          prepData(result, resultIndex, data.TravelAvailabilityResponses.length)
        );

        if (data.Packages && data.Packages.length) {
          service.packageResults = pkgPrepData(data, trips);
          service.hasPackages = true;
        }
        trips.forEach((result, resultIndex) => {
          if (Number.isInteger(result.matchingResultsIndex)) {
            result.matchingResultsIndexes = getMatchingIndexes(resultIndex, result.matchingResultsIndex);
          }
        });

        service.results = trips;
        service.hasResults = trips.some((trip) => trip.searchResults.length > 0);
        service.hasLoaded = true;

        service.hasExchangeTicketNumber =
          service.hasResults &&
          trips.some((trip) =>
            trip.searchResults.some((sr) => Object.entries(sr.FareClasses).some(([key, val]) => val.ExchangeTicketNumber))
          );

        if (typeof callback === 'function') callback();
      })
      .catch(() => {
        service.hasBackendError = true;
        if (typeof callback === 'function') callback();
      });
  }

  function setResults(results) {
    service.results = results;
  }

  function getSeatMap(flights) {
    const deferred = $q.defer();
    const apiUrl = apiService.getUrl('travelSeatMap');

    $http
      .post(apiUrl, {
        SeatMapSearches: flights,
      })
      .then((response) => {
        let data = response.data;
        deferred.resolve(data);
      })
      .catch(() => {
        deferred.reject();
      });

    return deferred.promise;
  }

  function getFareClassGroupTypes(flight) {
    const groupTypes = {};
    _.forEach(flight.FareClasses, (fareClass) => {
      if (fareClass) {
        if (!groupTypes[fareClass.GroupType]) {
          groupTypes[fareClass.GroupType] = fareClass.ResBookDesignCode[0];
        }
      }
    });

    return _.isEmpty(groupTypes) ? undefined : groupTypes;
  }
  function getFareClassGroupAndBrandTypes(flight, index) {
    const groupTypes = {};
    _.forEach(flight.FareClasses, (fareClass) => {
      if (fareClass) {
        groupTypes[fareClass.GroupType] = {
          BookingCode: fareClass.ResBookDesignCode[index],
          BrandId: fareClass.Segments[index] && fareClass.Segments[index].BrandId,
        };
      }
    });

    return _.isEmpty(groupTypes) ? undefined : groupTypes;
  }

  function getSeatmapSearch(gdsProvider, from, to, outboundFlight, inboundFlight, parentFlight, index) {
    return {
      OriginLocationCode: from,
      DestinationLocationCode: to,
      FlightNumber: outboundFlight.TripNumber,
      CarrierCode: outboundFlight.Carrier,
      OperatingCarrierCode: outboundFlight.OperatingCarrier,
      OperatingCarrierFlightNumber: outboundFlight.OperatingCarrierTripNumber,
      DepartureDateTimeOffset: outboundFlight.DepartureDateTimeOffset,
      ArrivalDateTimeOffset: inboundFlight.ArrivalDateTimeOffset,
      GdsProvider: gdsProvider,
      FareClassGroupTypes: getFareClassGroupTypes(parentFlight),
      FareClassGroupBrandTypes: getFareClassGroupAndBrandTypes(parentFlight, index),
    };
  }

  function getSeatmapSearches(flight) {
    const searches = [];

    if (flight.Legs.length === 0) {
      searches.push(
        getSeatmapSearch(flight.GdsProvider, flight.OriginLocationCode, flight.DestinationLocationCode, flight, flight, flight, 0)
      );
    } else {
      searches.push(
        getSeatmapSearch(flight.GdsProvider, flight.OriginLocationCode, flight.Legs[0].Origin.Code, flight, flight.Legs[0], flight, 0)
      );

      _.forEach(flight.Legs, (leg, legIndex) => {
        if (legIndex === flight.Legs.length - 1) {
          searches.push(
            getSeatmapSearch(flight.GdsProvider, leg.Origin.Code, flight.DestinationLocationCode, leg, flight, flight, legIndex + 1)
          );
        } else {
          searches.push(
            getSeatmapSearch(
              flight.GdsProvider,
              leg.Origin.Code,
              flight.Legs[legIndex + 1].Origin.Code,
              leg,
              flight.Legs[legIndex + 1],
              flight,
              legIndex + 1
            )
          );
        }
      });
    }

    return searches;
  }

  function getTravelDetail(travel) {
    const deferred = $q.defer();

    if (travel.Details.length) {
      deferred.resolve(travel.Details);
    } else {
      const apiUrl = apiService.getUrl('travelDetail', {
        type: travel.Type,
        travelId: travel.Identifier,
      });

      const params = {
        searchIdentifier: travel.SearchIdentifier,
      };

      $http
        .get(apiUrl, { params: params })
        .then((response) => {
          let data = response.data;
          travel.Details = data;
          deferred.resolve(travel.Details);
        })

        .catch(() => {
          deferred.reject();
        });
    }

    return deferred.promise;
  }

  function getFareRules(sector, fare, fares) {
    const deferred = $q.defer();

    if (fare.RemarksCollection) {
      const fareRuleData = () => {
        let text = '';
        _.forEach(fare.RemarksCollection, (obj) => {
          text = text + obj.Code + '\n' + obj.Value + '\n\n';
        });
        return text;
      };

      const fareRules = {
        Type: 'Text',
        Data: fareRuleData(),
      };

      deferred.resolve(fareRules);
    } else {
      const apiUrl = apiService.getUrl('travelFareRules', {
        type: sector.Type,
        travelId: sector.Identifier,
        fareId: fare.Identifier,
      });

      const params = {
        searchIdentifier: sector.SearchIdentifier,
      };

      $http
        .get(apiUrl, { params: params })
        .then((response) => {
          let data = response.data;
          deferred.resolve(data);
        })

        .catch(() => {
          deferred.reject();
        });
    }

    return deferred.promise;
  }

  function formatBundle(bundle, baseFare, index) {
    let bundleItems = bundle.Description.split(/\s*and\s*|,\s*/);
    let formattedBundle = {
      id: index,
      brandedFareName: baseFare.FareName,
      items: bundleItems,
      price: bundle.Price,
      currencyCode: bundle.CurrencyCode,
      selected: false,
    };
    return formattedBundle;
  }

  function prepData(data, index, totalSets) {
    let obj = {
      selected: false,
      index: index,
      searchIdentifier: data.Identifier,
      matchingResultsIndex: data.MatchingResultsIndex,
      searchDate: data.Date,
      destination: data.Destination,
      origin: data.Origin,
      searchResults: data.Results,
      classes: [],
      economyClasses: [],
      activeSort: null,
      resultLimitIncrement: 20,
      resultLimit: getResultLimit(totalSets),
    };
    data.Results.forEach((item, i) => {
      let fareClasses = item.FareClasses;

      item.$state = { visible: true };
      item.TotalFares = 0;
      item.SupplierName = item.SupplierName || item.CarrierName;
      item.ReturnPricing = Number.isInteger(obj.matchingResultsIndex);
      item.DefaultIndex = i;
      item.TotalLayoverInMinutes = item.Details.reduce((total, leg) => total + dateTimeService.layoverInMinutes(leg.Layover), 0);

      if (item.Rating) {
        item.Rating.OnTimeLevel = item.Rating.OntimePercent < 0.7 ? 'low' : item.Rating.OntimePercent < 0.9 ? 'mid' : 'high';
      }

      angular.forEach(fareClasses, (fare, key) => {
        if (fare === null) {
          return delete fareClasses[key];
        }

        fare.$state = { visible: true };
        fare.Class = key;

        const defaultFare = _.omit(fare, 'AdditionalFares');

        if (APP_CONFIG.bundles.enabled) {
          defaultFare.Bundles = _.map(fare.Bundles, (bundle, index) => formatBundle(bundle, fare, index));
        }

        fare.AdditionalFares.unshift(defaultFare);
        fare.AdditionalFares = _.map(fare.AdditionalFares, (additionalFare) => {
          additionalFare.$state = { visible: true };
          additionalFare.FareCode = additionalFare.FareCode ? additionalFare.FareCode.trim() : '';
          additionalFare.FareCodeDescription = additionalFare.FareCode ? `(${additionalFare.FareCode})` : '';
          additionalFare.MatchingResultsIndex = data.MatchingResultsIndex;
          additionalFare.Class = key;
          additionalFare.Bundles = APP_CONFIG.bundles.enabled
            ? _.map(additionalFare.Bundles, (bundle, index) =>
              bundle.items != null ? bundle : formatBundle(bundle, additionalFare, index)
            )
            : [];

          return additionalFare;
        });

        if (fare.GroupType === 'Economy') {
          if (!obj.economyClasses.includes(fare.Class)) {
            obj.economyClasses.push(fare.Class);
          }
        }

        fare.DisplayFare = fare.AdditionalFares[0];

        item.TotalFares += fare.AdditionalFares.length;
      });

      obj.classes = obj.classes.concat(Object.keys(fareClasses));
      item.TripDays = calculateDayChanges(item.DepartureDateTime, item.ArrivalDateTime);
    });

    markCheapest(data.Results);

    buildFareClasses(obj.classes);
    obj.classes = service.fareClasses;
    orderSelectedItems(data, index);

    return obj;
  }

  function markCheapestUsingCheapestOptions(results) {
    let cheapestFlightIds = [];

    //Getting Cheapest Fare Options
    results.forEach((sr) => {
      Object.entries(sr.FareClasses)
        .filter(([fcName, fc]) => fc)
        .forEach(([fcName, fc]) => {
          const { PolicyExceptionDetails } = fc;
          if (PolicyExceptionDetails.CheapestOptions) {
            PolicyExceptionDetails.CheapestOptions.forEach((co) => {
              cheapestFlightIds.push(co.FareIdentifier);
            });
          }
        });
    });

    //Deleting duplicates
    cheapestFlightIds = new Set(cheapestFlightIds.map((item) => item));

    //Marking Cheapest Fares
    results.forEach((sr) => {
      {
        Object.entries(sr.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.AdditionalFares.forEach((fare) => {
              fare.Cheapest = cheapestFlightIds.has(fare.Identifier);
            });
          });
      }
    });

    return cheapestFlightIds.size > 0;
  }

  function markCheapest(results) {
    if (markCheapestUsingCheapestOptions(results)) return;
    const distinctCarriers = new Set();
    results.forEach((sr) => distinctCarriers.add(sr.Carrier));
    const carriers = [...distinctCarriers];
    const cheapestFares = {};
    results.forEach((sr) => {
      const { Carrier } = sr;
      if (carriers.includes(Carrier)) {
        Object.entries(sr.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.AdditionalFares.forEach((fare) => {
              const { Amount } = fare;
              if (!cheapestFares[Carrier] || Amount < cheapestFares[Carrier].Amount) {
                cheapestFares[Carrier] = { fcName, Amount };
              }
            });
          });
      }
    });

    results.forEach((sr) => {
      const { Carrier } = sr;
      if (carriers.includes(Carrier)) {
        Object.entries(sr.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.AdditionalFares.forEach((fare) => {
              fare.Cheapest = fare.Amount === cheapestFares[Carrier].Amount;
            });
          });
      }
    });
  }

  function calculateDayChanges(departureDateTime, arrivalDateTime) {
    const depart = new Date(departureDateTime);
    const arrive = new Date(arrivalDateTime);
    if (isNaN(depart.getTime()) || isNaN(arrive.getTime())) return 0;

    const departDate = new Date(depart.getFullYear(), depart.getMonth(), depart.getDate());
    const arriveDate = new Date(arrive.getFullYear(), arrive.getMonth(), arrive.getDate());

    const diff = arriveDate.getTime() - departDate.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  function orderSelectedItems(data, index) {
    if (!($stateParams.selectedItems && $stateParams.selectedItems.length > 0 && data && data.Results)) return;
    for (let i = 0; i < $stateParams.selectedItems.length; i++) {
      for (let r = 0; r < data.Results.length; r++) {
        if ($stateParams.selectedItems[i] && data.Results[r].TripIdentifier === $stateParams.selectedItems[i].TripIdentifier) {
          data.Results.splice(0, 0, data.Results.splice(r, 1)[0]);
          $stateParams.selectedItems[i].SearchIdentifier = data.Results[r].SearchIdentifier;
          $stateParams.selectedItems[i].SearchIndex = index;
          return;
        }
      }
    }
  }

  function getResultLimit(totalSets) {
    const MAX_TOTAL = 100;

    return totalSets === 3 ? 30 : MAX_TOTAL / totalSets;
  }

  function orderFareClasses(fareClasses) {
    const fareBucketClasses = settingsService.settings.FlightConfiguration.FareBucketClasses;

    if (!fareBucketClasses) {
      return fareClasses;
    }

    return _.orderBy(fareClasses, (fareClass) => {
      const fareBucketClassSettings = _.find(fareBucketClasses, (bucketClass) => {
        return bucketClass.Name === fareClass;
      });

      return fareBucketClassSettings ? fareBucketClassSettings.Sort : Number.MAX_SAFE_INTEGER;
    });
  }

  function buildFareClasses(classes) {
    service.fareClasses = service.fareClasses.concat(classes);
    service.fareClasses = _.uniq(service.fareClasses);
    service.fareClasses = orderFareClasses(service.fareClasses);

    angular.forEach(service.results, (result) => {
      result.classes = service.fareClasses;
    });
  }

  function getMatchingIndexes(currentIndex, matchingIndex) {
    let resultsSet = service.results[matchingIndex];
    let indexes = [matchingIndex];

    if (service.results.length > 2) {
      service.results.forEach((item, i) => {
        if (
          (item.matchingResultsIndex == matchingIndex ||
            item.matchingResultsIndex == currentIndex ||
            item.orderBy == resultsSet.matchingResultsIndex) &&
          i !== currentIndex &&
          indexes.indexOf(i) === -1
        ) {
          indexes.push(i);
        }
      });
    }
    return indexes;
  }

  function getFareIsOutOfPolicyMsg(fare) {
    let msg = '';
    let separator = '<br/>';
    if (!(fare && fare.PolicyExceptionDetails)) return msg;

    if (fare.PolicyExceptionDetails.InPolicy === false) {
      if (fare.PolicyExceptionDetails.PolicyMessage) {
        msg += (msg != '' ? separator : '') + fare.PolicyExceptionDetails.PolicyMessage;
      } else {
        msg += (msg != '' ? separator : '') + 'Out of Policy: Lost Savings';
      }
    }
    if (fare.PolicyExceptionDetails.PolicyViolations && fare.PolicyExceptionDetails.PolicyViolations.length > 0) {
      service.setPolicyMsgViolations(fare.PolicyExceptionDetails);
      msg +=
        (msg != '' ? separator : '') +
        fare.PolicyExceptionDetails.PolicyViolations.map((policy) =>
          policy.ViolationMessage ? policy.ViolationMessage : 'Out of Policy'
        ).join(separator);
    }
    return msg;
  }

  function setPolicyMsgViolations(policyExceptionDetails) {
    if (
      service.policyViolations &&
      service.policyViolations.length &&
      policyExceptionDetails &&
      policyExceptionDetails.PolicyViolations &&
      policyExceptionDetails.PolicyViolations.length
    ) {
      let items = [];
      policyExceptionDetails.PolicyViolations.forEach((item) => {
        let lookupUp = service.policyViolations.find((f) => f.id === item.id);
        if (lookupUp) {
          items.push(lookupUp);
        } else {
          items.push(item);
        }
      });
      policyExceptionDetails.PolicyViolations = items;
    }

    if (
      service.policyMessagePopups &&
      service.policyMessagePopups.length &&
      policyExceptionDetails &&
      policyExceptionDetails.PolicyMessagePopups &&
      policyExceptionDetails.PolicyMessagePopups.length
    ) {
      let items = [];
      policyExceptionDetails.PolicyMessagePopups.forEach((item) => {
        let lookupUp = service.policyMessagePopups.find((f) => f.id === item.id);
        if (lookupUp) {
          items.push(lookupUp);
        } else {
          items.push(item);
        }
      });
      policyExceptionDetails.PolicyMessagePopups = items;
    }
  }

  function findFare(fareIdentifier, trips) {
    for (const trip of trips) {
      for (const result of trip.searchResults) {
        const foundFare = Object.values(result.FareClasses).find((fare) => {
          if (fare.Identifier === fareIdentifier) return fare;
          return fare.AdditionalFares.find((af) => {
            if (af.Identifier === fareIdentifier) return af;
          });
        });
        if (foundFare) return foundFare;
      }
    }
    return {};
  }

  function getAllFares(trips) {
    let fares = [];
    for (const trip of trips) {
      for (const result of trip.searchResults) {
        const foundFare = Object.values(result.FareClasses).find((fare) => {
          fares.push(fare);
          return fare.AdditionalFares.find((af) => {
            fares.push(af);
          });
        });
      }
    }
    return fares;
  }

  function pkgPrepData(data, trips) {
    service.hasPackages = true;
    service.pkgFareClasses = [...new Set(data.Packages.flatMap((pkg) => Object.keys(pkg.FareClasses)))];

    const order = settingsService.settings.FlightConfiguration.FareBucketClasses;
    order.sort((a, b) => a.Sort - b.Sort);
    service.pkgFareClasses = order.filter((item) => service.pkgFareClasses.includes(item.Name)).map((item) => item.Name);
    let allFares = getAllFares(trips);

    const packages = data.Packages.map((pkg, i) => {
      const flyPolicyExceptionDetails = {};
      for (const fc in pkg.FareClasses) {
        const fareClass = pkg.FareClasses[fc];
        fareClass.Details.PackageFares = fareClass.FareIdentifiers.map((f) => allFares.find((d) => d.Identifier === f));
        fareClass.Fares = [fareClass.Details];
        fareClass.AdditionalFares.forEach((af) => {
          af.Details.PackageFares = af.FareIdentifiers.map((f) => allFares.find((d) => d.Identifier === f));
          fareClass.Fares.push(af.Details);
        });
        fareClass.Fares.forEach((fare) => (fare.visible = true));
        fareClass.Fares.sort((a, b) => a.Amount - b.Amount);
        fareClass.DisplayFare = fareClass.Fares[0];
        fareClass.visible = true;
        if (fareClass.Details.PolicyExceptionDetails) {
          flyPolicyExceptionDetails.FlyAmericaActComplianceType = fareClass.Details.PolicyExceptionDetails.FlyAmericaActComplianceType;
          flyPolicyExceptionDetails.FlyAmericaActComplianceExceptionReason =
            fareClass.Details.PolicyExceptionDetails.FlyAmericaActComplianceExceptionReason;
          flyPolicyExceptionDetails.FlyAmericaActInPolicy = fareClass.Details.PolicyExceptionDetails.FlyAmericaActInPolicy;
        }
      }

      pkg.FareClasses.$state = {};

      const packageFares = service.pkgFareClasses.reduce((fares, fareClass) => {
        fares[fareClass] = pkg.FareClasses[fareClass] || null;
        return fares;
      }, {});

      const cleanPackage = {
        FareClasses: packageFares,
        Segments: [],
        visible: true,
        cheapest: false,
        defaultSort: i,
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
        flyPolicyExceptionDetails: flyPolicyExceptionDetails,
      };

      pkg.Segments.forEach((segment) => {
        const resultTrip = data.TravelAvailabilityResponses.find((trip) => trip.Identifier === segment.TripIdentifier);
        if (!resultTrip) return;

        const cleanSegment = {
          origin: resultTrip.Origin,
          destination: resultTrip.Destination,
          identifier: resultTrip.Identifier,
          visible: true,
        };

        const resultFlight = pkgFindFlight(segment.FlightIdentifier, resultTrip.Results);
        if (!resultFlight) return;

        resultFlight.TripDays = calculateDayChanges(resultFlight.DepartureDateTime, resultFlight.ArrivalDateTime);

        cleanSegment.flight = pkgPrepFlight(resultFlight);

        if (!cleanPackage.safScore) cleanPackage.safScore = cleanSegment.flight.SafScore;

        cleanPackage.Segments.push(cleanSegment);
      });

      return cleanPackage;
    });

    pkgMarkCheapest(packages);
    pkgMarkPreferred(packages);

    return packages;
  }

  function pkgMarkPreferred(packages) {
    packages.forEach((pkg) => (pkg.preferred = pkg.Segments.some((segment) => segment.flight.Preferred)));
  }

  function pkgMarkCheapestUsingCheapestOptions(packages) {
    let cheapestFlightIds = [];

    //Getting Cheapest Fare Options
    packages.forEach((pkg) => {
      Object.entries(pkg.FareClasses)
        .filter(([fcName, fc]) => fc)
        .forEach(([fcName, fc]) => {
          fc.Fares.forEach((fare) => {
            const { PolicyExceptionDetails } = fare;
            if (PolicyExceptionDetails.CheapestOptions) {
              PolicyExceptionDetails.CheapestOptions.forEach((co) => {
                cheapestFlightIds.push(co.FareIdentifier);
              });
            }
          });
        });
    });

    //Deleting duplicates
    cheapestFlightIds = new Set(cheapestFlightIds.map((item) => item));

    //Marking Cheapest Fares
    packages.forEach((pkg) => {
      Object.entries(pkg.FareClasses)
        .filter(([fcName, fc]) => fc)
        .forEach(([fcName, fc]) => {
          fc.Fares.forEach((fare) => {
            fare.PackageFares.forEach((pf) => {
              if (!fare.Cheapest) {
                fare.Cheapest = cheapestFlightIds.has(pf.Identifier);
              }
            });
          });
        });
    });

    return cheapestFlightIds.size > 0;
  }

  function pkgMarkCheapest(packages) {
    if (pkgMarkCheapestUsingCheapestOptions(packages)) return;
    const distinctCarriers = new Set();
    packages.forEach((pkg) => pkg.Segments.forEach((segment) => distinctCarriers.add(segment.flight.Carrier)));
    const carriers = [...distinctCarriers];
    const cheapestFares = {};
    packages.forEach((pkg) => {
      pkg.Segments.forEach((segment) => {
        const { Carrier } = segment.flight;
        Object.entries(pkg.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.Fares.forEach((fare) => {
              const { Amount } = fare;
              if (!cheapestFares[Carrier] || Amount < cheapestFares[Carrier].Amount) {
                cheapestFares[Carrier] = { fcName, Amount };
              }
            });
          });
      });
    });

    packages.forEach((pkg) => {
      pkg.Segments.forEach((segment) => {
        const { Carrier } = segment.flight;
        Object.entries(pkg.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.Fares.forEach((fare) => {
              fare.Cheapest = fare.Amount === cheapestFares[Carrier].Amount;
            });
          });
      });
    });
  }

  function pkgPrepFlight(flight) {
    const equiptmentTypeArray = flight.AircraftName.split('/');
    if (flight.Details && flight.Details.length === equiptmentTypeArray.length) {
      for (let i = 0; i < flight.Details.length; i++) {
        if (!flight.Details[i].EquipmentType) flight.Details[i].EquipmentType = equiptmentTypeArray[i];
      }
    }

    return flight;
  }

  function pkgFindFlight(flightIdentifier, flights) {
    return flights.find((flight) => flight.Identifier === flightIdentifier);
  }

  function hasRestrictedDestination(flightSearchForm) {
    if (flightSearchForm && flightSearchForm.flight1Departure) {
      const totalFlights = flightSearchForm.flightsAmount.$modelValue;
      const tripOrigin = flightSearchForm.flight1Departure.$modelValue;

      if (tripOrigin) {
        for (let i = 2; i <= totalFlights; i++) {
          const thisFlight = flightSearchForm[`flight${i}Departure`].$modelValue;

          if (thisFlight && thisFlight.Value === tripOrigin.Value) {
            return tripOrigin.Value;
          }
        }
      }
    }

    return false;
  }

  function hasRestrictedRoute(flightSearchForm) {
    if (flightSearchForm) {
      const totalFlights = flightSearchForm.flightsAmount.$modelValue;
      const arrivalDepartures = [];

      for (let i = 1; i <= totalFlights; i++) {
        const thisArrival = flightSearchForm[`flight${i}Arrival`].$modelValue;
        const thisDeparture = flightSearchForm[`flight${i}Departure`].$modelValue;

        if (thisArrival && thisDeparture) {
          let thisArrivalDeparture;

          // Both are CustomHub - compare by Group
          if (thisArrival.AirportLocationType === 'CustomHub' && thisDeparture.AirportLocationType === 'CustomHub') {
            thisArrivalDeparture = `${thisDeparture.Group}/${thisArrival.Group}`;
          }
          // Both are regular airports - compare by Value
          else if (thisArrival.AirportLocationType !== 'CustomHub' && thisDeparture.AirportLocationType !== 'CustomHub') {
            thisArrivalDeparture = `${thisDeparture.Value}/${thisArrival.Value}`;
          }
          // Mixed case - skip this iteration (no restriction)
          else {
            continue;
          }

          if (arrivalDepartures.includes(thisArrivalDeparture)) {
            return thisArrivalDeparture;
          }

          arrivalDepartures.push(thisArrivalDeparture);
        }
      }
    }

    return false;
  }

  function hasInvalidRestrictedDestinationValidator(frm) {
    if (!frm.$error || !frm.$error.required) return false;
    for (let i = 0; i < frm.$error.required.length; i++) {
      if (!frm.$error.required[i].restrictedDestinationValidator) continue;
      if (frm.$error.required[i].restrictedDestinationValidator.$invalid) return true;
    }
    return false;
  }

  function getArray(num) {
    if (!num) {
      return [];
    }

    return new Array(num);
  }

  service.isVisibleOrOnlyFilteredBy = function isVisibleOrOnlyFilteredBy(flight, fare, id) {
    if (!flight.visible && flight.filteredBySegments) return false;

    return CheckFilterState(flight, fare, id);
  };
  service.GetIdSeg = function GetIdSeg(id, i) {
    return `${id}_${i}`;
  };
  service.isVisibleOrOnlyFilteredBySegment = function isVisibleOrOnlyFilteredBySegment(pkg, segment, id, i) {
    if (!pkg.visible && pkg.filteredByFares) return false;
    return CheckFilterState(pkg, segment, service.GetIdSeg(id, i));
  };
  function CheckFilterState(flight, fare, id) {
    if (flight.visible && (!fare || fare.visible)) return true;
    let hasFilters = flight.filteredBy && flight.filteredBy.length > 0;
    let isOnlyFilteredById = hasFilters && flight.filteredBy.length === 1 && flight.filteredBy[0] === id;

    if (!fare || (hasFilters && !isOnlyFilteredById)) {
      return isOnlyFilteredById;
    }
    let fareFiltered = fare && fare.filteredBy && fare.filteredBy.length === 1 && fare.filteredBy[0] === id;

    return fareFiltered;
  }
  function showPackages() {
    if (forceShowPackages()) return true;
    let showPackagesConfig = getPkgViewToggleDefaultOn();
    let showPackagesStorage = localStorage.getItem('showPackages');
    let sp = showPackagesStorage !== null ? showPackagesStorage === 'true' : showPackagesConfig;
    return sp;
  }

  function forceShowPackages() {
    return getPkgViewToggleDefaultOn() === true && !getPkgViewToggleText();
  }
  function setShowPackages(val) {
    localStorage.setItem('showPackages', val);
  }
  function getPkgViewToggleText() {
    if (
      settingsService.settings &&
      settingsService.settings.FlightConfiguration &&
      settingsService.settings.FlightConfiguration.PkgViewToggleText
    ) {
      return settingsService.settings.FlightConfiguration.PkgViewToggleText;
    }
    return null;
  }
  function getPkgViewToggleDefaultOn() {
    if (
      settingsService.settings &&
      settingsService.settings.FlightConfiguration &&
      settingsService.settings.FlightConfiguration.PkgViewToggleDefaultOn
    ) {
      return settingsService.settings.FlightConfiguration.PkgViewToggleDefaultOn === true;
    }
    return null;
  }

  function getCleanFare(fare) {
    if (!fare) return null;
    return {
      GdsProvider: fare.GdsProvider,
      Class: fare.Class,
      FareCode: fare.FareCode,
      FareDescription: fare.FareDescription,
      FareName: fare.FareName,
      Refundable: fare.Refundable,
    };
  }

  function isMatchingFare(fare1, fare2) {
    if (!fare1 || !fare2) return false;
    if (fare1.GdsProvider !== fare2.GdsProvider) return false;
    if (fare1.Class !== fare2.Class) return false;
    if (fare1.FareCode !== fare2.FareCode) return false;
    if (fare1.FareDescription !== fare2.FareDescription) return false;
    if (fare1.FareName !== fare2.FareName) return false;
    if (fare1.Refundable !== fare2.Refundable) return false;
    return true;
  }
  return service;
};

travelService.$inject = $inject;

export default travelService;



// WEBPACK FOOTER //
// ./src/services/travelService.js